<ng-template #content let-offcanvas>
  <div class="offcanvas-header border-bottom border-dark p-2">
    <div class="d-flex align-items-center gap-3 flex-wrap w-100">
      <h5 class="offcanvas-title text-nowrap mb-0 me-2">Visual Analysis</h5>
      <div class="d-flex align-items-center gap-2">
        <label class="text-nowrap mb-0">Hierarchy:</label>
        <select
          class="form-control form-control-sm"
          [(ngModel)]="rootLevel"
          (change)="onHierarchyChange()"
        >
          <option value="Segment">Segment &rsaquo; Brand &rsaquo; Product</option>
          <option value="Family">Family &rsaquo; Brand &rsaquo; Product</option>
        </select>
      </div>
      <div class="d-flex align-items-center gap-3">
        <div class="form-check form-switch">
          <input
            class="form-check-input"
            type="radio"
            value="sales"
            id="salesSwitch"
            name="metricSwitch"
            [(ngModel)]="selectedMetric"
            (change)="updateAnalysis()"
          />
          <label class="form-check-label" for="salesSwitch">Sales</label>
        </div>
        <div class="form-check form-switch">
          <input
            class="form-check-input"
            type="radio"
            value="salesQty"
            id="salesQtySwitch"
            name="metricSwitch"
            [(ngModel)]="selectedMetric"
            (change)="updateAnalysis()"
          />
          <label class="form-check-label" for="salesQtySwitch">Volume</label>
        </div>
        <button
          type="button"
          class="btn btn-sm btn-outline-success ms-auto"
          (click)="exportToExcel()"
          [disabled]="hierarchy.length === 0"
          title="Export analysis to Excel"
        >
          <i class="bi bi-file-earmark-spreadsheet me-1"></i>Export
        </button>
      </div>
      <button
        type="button"
        class="btn-close me-1"
        aria-label="Close"
        (click)="close()"
      ></button>
    </div>
  </div>

  <div class="offcanvas-body va-panes">
    @if (hierarchy.length === 0) {
      <div class="text-center text-muted p-4 w-100">
        No products on the planogram to analyse.
      </div>
    } @else {
      <!-- ===================== Segment / Family pane ===================== -->
      <div class="va-pane">
        <div class="va-pane-title va-title-segment">
          <button
            class="btn btn-sm btn-link p-0 me-2 text-light"
            (click)="toggleAllGroupsVisibility()"
            [title]="areAllGroupsVisible() ? 'Hide all' : 'Show all'"
          >
            <i
              class="bi"
              [ngClass]="areAllGroupsVisible() ? 'bi-eye-slash' : 'bi-eye'"
            ></i>
          </button>
          {{ rootLevelLabel }}
        </div>
        <div class="va-pane-scroll">
          <table class="table table-sm table-hover va-table mb-0">
            <thead>
              <tr>
                <th></th>
                <th>Name</th>
                <th class="text-end">{{ selectedMetric === 'sales' ? 'Sales %' : 'Vol %' }}</th>
                <th class="text-end">Space %</th>
                <th class="text-end">DOS</th>
                <th class="text-end">Fac</th>
                <th class="text-end">Fac R</th>
                <th class="text-end"># Prod</th>
              </tr>
            </thead>
            <tbody>
              @for (segment of hierarchy; track segment.key) {
                <tr
                  class="va-clickable"
                  [class.table-active]="segment.key === selectedSegmentKey"
                  [class.text-muted]="!segment.visible"
                  (click)="selectSegment(segment)"
                >
                  <td class="text-center">
                    <button
                      class="btn btn-sm btn-link p-0"
                      (click)="toggleSegmentVisibility(segment); $event.stopPropagation()"
                      [title]="segment.visible ? 'Hide' : 'Show'"
                    >
                      <i
                        class="bi"
                        [ngClass]="segment.visible ? 'bi-eye-slash-fill' : 'bi-eye'"
                      ></i>
                    </button>
                  </td>
                  <td class="va-name fw-semibold">{{ segment.key }}</td>
                  <td class="text-end">{{ segment.salesPercentage | number: '1.0-1' }}%</td>
                  <td class="text-end">{{ segment.spacePercentage | number: '1.0-1' }}%</td>
                  <td class="text-end">{{ segment.dos | number: '1.0-1' }}</td>
                  <td class="text-end">{{ segment.facings }}</td>
                  <td class="text-end">
                    @if (segment.facings_recommended > segment.facings) {
                      <i class="bi bi-arrow-up-short text-success"></i>
                    }
                    @if (segment.facings_recommended < segment.facings) {
                      <i class="bi bi-arrow-down-short text-danger"></i>
                    }
                    {{ segment.facings_recommended | number: '1.0-0' }}
                  </td>
                  <td class="text-end">{{ segment.count }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>

      <!-- ========================== Brand pane =========================== -->
      <div class="va-pane">
        <div class="va-pane-title va-title-brand">
          Brand
          @if (selectedSegment) {
            <span class="va-title-context">· {{ selectedSegment.key }}</span>
          }
        </div>
        <div class="va-pane-scroll">
          <table class="table table-sm table-hover va-table mb-0">
            <thead>
              <tr>
                <th></th>
                <th class="text-center">Color</th>
                <th>Name</th>
                <th class="text-end">{{ selectedMetric === 'sales' ? 'Sales %' : 'Vol %' }}</th>
                <th class="text-end">Space %</th>
                <th class="text-end">DOS</th>
                <th class="text-end">Fac</th>
                <th class="text-end">Fac R</th>
                <th class="text-end"># Prod</th>
              </tr>
            </thead>
            <tbody>
              @for (brand of selectedSegment?.brands ?? []; track brand.uniqueKey) {
                <tr
                  class="va-clickable"
                  [class.table-active]="brand.uniqueKey === selectedBrandKey"
                  [class.text-muted]="!brand.visible || !selectedSegment!.visible"
                  (click)="selectBrand(brand)"
                >
                  <td class="text-center">
                    <button
                      class="btn btn-sm btn-link p-0"
                      (click)="toggleBrandVisibility(selectedSegment!, brand); $event.stopPropagation()"
                      [disabled]="!selectedSegment!.visible"
                      [title]="brand.visible ? 'Hide' : 'Show'"
                    >
                      <i
                        class="bi"
                        [ngClass]="brand.visible ? 'bi-eye-slash-fill' : 'bi-eye'"
                      ></i>
                    </button>
                  </td>
                  <td class="text-center" (click)="$event.stopPropagation()">
                    <input
                      class="color-box"
                      readonly
                      [(colorPicker)]="brand.color"
                      [style.background]="brand.color"
                      (colorPickerChange)="onBrandColorChange(selectedSegment!, brand)"
                    />
                  </td>
                  <td class="va-name fw-semibold">{{ brand.key }}</td>
                  <td class="text-end">{{ brand.salesPercentage | number: '1.0-1' }}%</td>
                  <td class="text-end">{{ brand.spacePercentage | number: '1.0-1' }}%</td>
                  <td class="text-end">{{ brand.dos | number: '1.0-1' }}</td>
                  <td class="text-end">{{ brand.facings }}</td>
                  <td class="text-end">
                    @if (brand.facings_recommended > brand.facings) {
                      <i class="bi bi-arrow-up-short text-success"></i>
                    }
                    @if (brand.facings_recommended < brand.facings) {
                      <i class="bi bi-arrow-down-short text-danger"></i>
                    }
                    {{ brand.facings_recommended | number: '1.0-0' }}
                  </td>
                  <td class="text-end">{{ brand.count }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>

      <!-- ========================= Product pane ========================== -->
      <div class="va-pane va-pane-product">
        <div class="va-pane-title va-title-product">
          Product
          @if (selectedBrand) {
            <span class="va-title-context">· {{ selectedBrand.key }}</span>
          }
        </div>
        <div class="va-pane-scroll">
          <table class="table table-sm va-table mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Barcode</th>
                <th>Size</th>
                <th class="text-end">{{ selectedMetric === 'sales' ? 'Sales %' : 'Vol %' }}</th>
                <th class="text-end">Space %</th>
                <th class="text-end">DOS</th>
                <th class="text-end">Fac</th>
                <th class="text-end">Fac R</th>
              </tr>
            </thead>
            <tbody>
              @for (row of selectedBrand?.products ?? []; track row.product.productId) {
                <tr>
                  <td class="va-name">{{ row.name }}</td>
                  <td>{{ row.barcode }}</td>
                  <td class="text-nowrap">{{ row.size }}</td>
                  <td class="text-end">{{ row.salesPercentage | number: '1.0-1' }}%</td>
                  <td class="text-end">{{ row.spacePercentage | number: '1.0-1' }}%</td>
                  <td class="text-end">{{ row.dos | number: '1.0-1' }}</td>
                  <td class="text-end">{{ row.facings }}</td>
                  <td class="text-end">
                    @if (row.facings_recommended > row.facings) {
                      <i class="bi bi-arrow-up-short text-success"></i>
                    }
                    @if (row.facings_recommended < row.facings) {
                      <i class="bi bi-arrow-down-short text-danger"></i>
                    }
                    {{ row.facings_recommended | number: '1.0-0' }}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    }
  </div>
</ng-template>
